import { useState, useEffect, useCallback } from 'react'
import { authFetch } from '../lib/api'

const EVALUATION_STARS = { satisfactory: '⭐', good: '⭐⭐', awesome: '⭐⭐⭐' }

const TAG_COLORS = [
  'bg-violet-100 text-violet-700', 'bg-pink-100 text-pink-700',
  'bg-orange-100 text-orange-700', 'bg-teal-100 text-teal-700',
  'bg-cyan-100 text-cyan-700',     'bg-rose-100 text-rose-700',
  'bg-lime-100 text-lime-700',     'bg-amber-100 text-amber-700',
]
function tagColor(tag) {
  if (!tag) return 'bg-gray-100 text-gray-500'
  let h = 0
  for (const c of tag) h = (h * 31 + c.charCodeAt(0)) & 0xffff
  return TAG_COLORS[h % TAG_COLORS.length]
}

function formatDate(dateStr) {
  const d = new Date(dateStr)
  const today = new Date()
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1)

  const sameDay = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()

  const timeStr = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

  if (sameDay(d, today))     return `Today, ${timeStr}`
  if (sameDay(d, yesterday)) return `Yesterday, ${timeStr}`

  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  }) + `, ${timeStr}`
}

function groupByDay(entries) {
  const groups = []
  let currentDay = null
  let currentGroup = null

  for (const entry of entries) {
    const d     = new Date(entry.date)
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`

    if (dayKey !== currentDay) {
      currentGroup = { dayKey, label: formatDayLabel(d), entries: [] }
      groups.push(currentGroup)
      currentDay = dayKey
    }
    currentGroup.entries.push(entry)
  }
  return groups
}

function formatDayLabel(d) {
  const today     = new Date()
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1)

  const sameDay = (a, b) =>
    a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()

  if (sameDay(d, today))     return 'Today'
  if (sameDay(d, yesterday)) return 'Yesterday'

  return d.toLocaleDateString(undefined, {
    weekday: 'long', month: 'short', day: 'numeric',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

function EntryRow({ entry, hasMoney, kids }) {
  const timeStr = new Date(entry.date).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  })

  const isDebit  = entry.type === 'debit'
  const isPayout = isDebit

  return (
    <div className={`flex items-center gap-3 py-3 ${isPayout ? 'bg-red-50/60 -mx-4 px-4 rounded-lg' : ''}`}>
      {/* Icon */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
        isPayout
          ? 'bg-red-100 text-red-600'
          : kids
            ? 'bg-purple-100 text-purple-600'
            : 'bg-indigo-100 text-indigo-600'
      }`}>
        {isPayout ? '💸' : kids ? '⭐' : '✓'}
      </div>

      {/* Description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium truncate ${isPayout ? 'text-red-700' : 'text-gray-800'}`}>
            {entry.title}
          </span>
          {entry.tag && !isPayout && (
            <span className={`inline-flex text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${tagColor(entry.tag)}`}>
              {entry.tag}
            </span>
          )}
          {entry.evaluation && (
            <span className="text-xs text-gray-400 flex-shrink-0">
              {EVALUATION_STARS[entry.evaluation]}
            </span>
          )}
          {entry.paid_out && !isPayout && hasMoney && (
            <span className="text-xs text-gray-400 flex-shrink-0">paid out</span>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-0.5">{timeStr}</p>
      </div>

      {/* Amounts */}
      <div className="flex-shrink-0 text-right min-w-[5rem]">
        {isPayout ? (
          <span className="text-sm font-semibold text-red-600">
            − ₹{entry.debit.toFixed(2)}
          </span>
        ) : hasMoney && entry.credit != null ? (
          <span className={`text-sm font-semibold ${kids ? 'text-purple-600' : 'text-green-600'}`}>
            + ₹{entry.credit.toFixed(2)}
          </span>
        ) : hasMoney ? (
          <span className="text-xs text-gray-300">—</span>
        ) : null}
      </div>
    </div>
  )
}

export default function LedgerView({ namespace }) {
  const [entries,  setEntries]  = useState([])
  const [weeks,    setWeeks]    = useState(2)
  const [loading,  setLoading]  = useState(true)
  const [hasMore,  setHasMore]  = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const kids     = namespace?.slug === 'nishtha'
  const hasMoney = !!namespace?.pocket_money_enabled

  const load = useCallback(async (w, append = false) => {
    if (!namespace) return
    if (!append) setLoading(true); else setLoadingMore(true)

    try {
      const res  = await authFetch(`/api/ledger?namespace=${namespace.slug}&weeks=${w}`)
      if (!res.ok) return
      const data = await res.json()
      setEntries(data.entries)
      // If we got any entries at all, assume there could be more
      setHasMore(data.entries.length > 0)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [namespace])

  useEffect(() => {
    setEntries([])
    setWeeks(2)
    load(2)
  }, [namespace, load])

  function handleShowMore() {
    const next = weeks + 1
    setWeeks(next)
    load(next, true)
  }

  // Calculate running totals
  const totalCredit = entries.reduce((s, e) => e.type === 'credit' && e.credit != null ? s + e.credit : s, 0)
  const totalDebit  = entries.reduce((s, e) => e.type === 'debit' ? s + e.debit : s, 0)

  const groups = groupByDay(entries)

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
        <svg className="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
        </svg>
        <span className="text-sm">Loading history…</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      {hasMoney && entries.length > 0 && (
        <div className={`rounded-xl border p-4 flex gap-4 ${
          kids ? 'bg-purple-50 border-purple-100' : 'bg-gray-50 border-gray-100'
        }`}>
          <div className="flex-1 text-center">
            <p className="text-xs text-gray-500 mb-1">Earned</p>
            <p className={`text-lg font-bold ${kids ? 'text-purple-700' : 'text-green-700'}`}>
              ₹{totalCredit.toFixed(2)}
            </p>
          </div>
          <div className="w-px bg-gray-200" />
          <div className="flex-1 text-center">
            <p className="text-xs text-gray-500 mb-1">Paid out</p>
            <p className="text-lg font-bold text-red-600">₹{totalDebit.toFixed(2)}</p>
          </div>
          <div className="w-px bg-gray-200" />
          <div className="flex-1 text-center">
            <p className="text-xs text-gray-500 mb-1">Net</p>
            <p className={`text-lg font-bold ${
              totalCredit - totalDebit >= 0
                ? kids ? 'text-purple-700' : 'text-green-700'
                : 'text-red-600'
            }`}>
              ₹{(totalCredit - totalDebit).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Period label */}
      <p className="text-xs text-gray-400 text-center">
        Showing last {weeks} {weeks === 1 ? 'week' : 'weeks'}
        {entries.length > 0 ? ` · ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}` : ''}
      </p>

      {/* Grouped entries */}
      {groups.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-sm text-gray-400">No completed chores in the last {weeks} {weeks === 1 ? 'week' : 'weeks'}.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(({ dayKey, label, entries: dayEntries }) => (
            <div key={dayKey} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {/* Day header */}
              <div className="px-4 py-2.5 border-b border-gray-50 bg-gray-50/70">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {label}
                </span>
              </div>
              {/* Entries */}
              <div className="px-4 divide-y divide-gray-50">
                {dayEntries.map(entry => (
                  <EntryRow
                    key={`${entry.type}-${entry.id}`}
                    entry={entry}
                    hasMoney={hasMoney}
                    kids={kids}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show more */}
      {hasMore && (
        <button
          onClick={handleShowMore}
          disabled={loadingMore}
          className={`w-full py-2.5 text-sm font-medium rounded-xl border transition
            disabled:opacity-60 ${
              kids
                ? 'border-purple-200 text-purple-600 hover:bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
        >
          {loadingMore ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              Loading…
            </span>
          ) : (
            `Show more (+ 7 days)`
          )}
        </button>
      )}
    </div>
  )
}
